import React, { useMemo, useCallback } from "react";
import moment from "moment";
import { NavLink, TaskLink } from "../components"; // <--- IMPORTED TaskLink
import { createCellRendererWithCopy } from "./copyCellContent";
import { timestampRenderer } from "./helpers";

/**
 * Centralized cell renderer factory for AG Grid
 * Supports: time, simple, json, workflowId, link, executions, taskCount, taskTypes, etc.
 * Use: const renderer = useCellRenderer("time", true)
 */

export function useCellRenderer(type, withCopy = true, extra = {}) {
  // === Formatters ===
  const formatTimeValue = (value) =>
    value ? moment(value).format("DD-MM-YYYY HH:mm:ss") : "";

  const formatJsonValue = (value, truncate = true) => {
    try {
      if (value == null || value === undefined) return "";
      const stringified = JSON.stringify(value);
      return truncate
        ? stringified.substring(0, 50) + (stringified.length > 50 ? "..." : "")
        : stringified;
    } catch (e) {
      return String(value);
    }
  };

  const formatJsonArray = (value) => {
    if (value == null || value === undefined) return "";
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    return JSON.stringify(value);
  };

  // === Core renderers ===
  const simpleValueRenderer = useCallback(
    (params) =>
      params.value == null || params.value === undefined
        ? ""
        : String(params.value),
    []
  );

  const timeRenderer = useCallback(
    (params) => formatTimeValue(params.value),
    []
  );

  const timestampValueRenderer = useCallback(
    (params) => timestampRenderer(params.value),
    []
  );

  const workflowIdRenderer = useCallback(
    (params) =>
      params.value ? (
        <NavLink path={`/execution/${params.value}`}>{params.value}</NavLink>
      ) : (
        ""
      ),
    []
  );

  const taskLinkRenderer = useCallback(
    (params) => {
      const workflowId = extra?.workflowId;
      if (!params.value) return "-";

      if (!workflowId) {
        console.error(
          "TaskLinkRenderer: workflowId not provided in 'extra' prop"
        );
        return params.value;
      }
      return <TaskLink workflowId={workflowId} taskId={params.value} />;
    },
    [extra] // Depend on the 'extra' object
  );

  const taskDefLinkRenderer = useCallback(
    (params) =>
      params.value ? (
        <NavLink path={`/taskDef/${params.value}`}>{params.value}</NavLink>
      ) : (
        "-"
      ),
    []
  );

  const linkRenderer = useCallback(
    (params) =>
      params.value ? (
        <NavLink path={`/workflowDef/${params.data?.name?.trim()}`}>
          {params.data?.workflowName?.trim() || params.data?.name?.trim()}
        </NavLink>
      ) : (
        ""
      ),
    []
  );

  const jsonRenderer = useCallback(
    (params) => formatJsonValue(params.value, true),
    []
  );

  const jsonFullRenderer = useCallback(
    (params) => formatJsonValue(params.value, false),
    []
  );

  const jsonArrayRenderer = useCallback(
    (params) => formatJsonArray(params.value),
    []
  );

  const executionsRenderer = useCallback(
    (params) => (
      <NavLink path={`/search?workflowType=${params.value?.trim()}`}>
        Query
      </NavLink>
    ),
    []
  );

  const taskTypesRenderer = useCallback((params) => {
    const tasks = params.data?.tasks;
    if (!Array.isArray(tasks) || tasks.length === 0) return "-";
    const types = [
      ...new Set(tasks.map((t) => t.name || t.type).filter(Boolean)),
    ];
    return types.join(", ");
  }, []);

  const taskCountRenderer = useCallback((params) => {
    const tasks = params.value;
    return Array.isArray(tasks) ? tasks.length.toString() : "0";
  }, []);

  // === Dynamic selection ===
  const renderer = useMemo(() => {
    switch (type) {
      case "time":
        return timeRenderer;
      case "timestamp":
        return timestampValueRenderer;
      case "workflowId":
        return workflowIdRenderer;
      case "taskLink":
        return taskLinkRenderer;
      case "taskDefLink":
        return taskDefLinkRenderer;
      case "link":
        return linkRenderer;
      case "json":
        return jsonRenderer;
      case "jsonFull":
        return jsonFullRenderer;
      case "jsonArray":
        return jsonArrayRenderer;
      case "executions":
        return executionsRenderer;
      case "taskTypes":
        return taskTypesRenderer;
      case "taskCount":
        return taskCountRenderer;
      case "simple":
      default:
        return simpleValueRenderer;
    }
  }, [
    type,
    timeRenderer,
    timestampValueRenderer,
    workflowIdRenderer,
    taskLinkRenderer,
    taskDefLinkRenderer,
    linkRenderer,
    jsonRenderer,
    jsonFullRenderer,
    jsonArrayRenderer,
    executionsRenderer,
    taskTypesRenderer,
    taskCountRenderer,
    simpleValueRenderer,
  ]);

  // === Wrap with copy support if requested ===
  const wrappedRenderer = useMemo(() => {
    return withCopy ? createCellRendererWithCopy(renderer) : renderer;
  }, [renderer, withCopy]);

  return wrappedRenderer;
}

export const tooltipGetters = {
  simple: (params) => (params.value == null ? "" : String(params.value)),
  time: (params) =>
    params.value ? moment(params.value).format("DD-MM-YYYY HH:mm:ss") : "",
  json: (params) => {
    try {
      return params.value ? JSON.stringify(params.value, null, 2) : "";
    } catch {
      return String(params.value || "");
    }
  },
  jsonFull: (params) => {
    try {
      return params.value ? JSON.stringify(params.value, null, 2) : "";
    } catch {
      return String(params.value || "");
    }
  },
  jsonArray: (params) => {
    if (params.value == null) return "";
    if (Array.isArray(params.value)) {
      return params.value.join(", ");
    }
    try {
      return JSON.stringify(params.value, null, 2);
    } catch {
      return String(params.value);
    }
  },
  taskTypes: (params) => {
    const tasks = params.value;
    if (!Array.isArray(tasks)) return "";
    const types = [
      ...new Set(tasks.map((t) => t.name || t.type).filter(Boolean)),
    ];
    return types.join(", ");
  },
  taskCount: (params) =>
    Array.isArray(params.value) ? `${params.value.length} tasks` : "0",
  workflowId: (params) => params.value || "",
  link: (params) => params.data?.workflowName || params.data?.name || "",
  taskLink: (params) => params.value || "",
  taskDefLink: (params) => params.value || "",
};
